/**
 * Data transformation utilities for mapping API responses to form data
 */

/**
 * Map referee data from API response to form structure
 */
export const mapRefereeData = (referees) => {
	if (!referees || !Array.isArray(referees)) {
		return {
			recommendation1: {},
			recommendation2: {},
		};
	}
	
	const mapSingleReferee = (referee) => {
		if (!referee) return {};
		
		return {
			id: referee.id,
			name: referee.name || '',
			email: referee.email || '',
			mobile: referee.mobile || '',
			designation: referee.designation || '',
			institution: referee.institution || '',
			institution_webpage: referee.institution_webpage || '',
			file: referee.letter_url || referee.file || null,
			response: referee.response || 'Pending',
			response_at: referee.response_at,
		};
	};
	
	return {
		recommendation1: mapSingleReferee(referees[0]),
		recommendation2: mapSingleReferee(referees[1]),
	};
};

/**
 * Map personal info from API response
 */
export const mapPersonalInfo = (personalInfoData) => {
	if (!personalInfoData) return {};
	
	// Helper function to parse date as local date (not UTC)
	const parseLocalDate = (dateString) => {
		if (!dateString) return new Date();
		
		// If it's already a Date object, return it
		if (dateString instanceof Date) return dateString;
		
		// Parse date string as local date to avoid timezone conversion issues
		// Extract year, month, day from the string (e.g., "1990-05-15" or "1990-05-15T00:00:00.000Z")
		const dateStr = dateString.split('T')[0]; // Get just the date part
		const [year, month, day] = dateStr.split('-').map(Number);
		
		// Create date in local timezone (month is 0-indexed in JS)
		return new Date(year, month - 1, day);
	};
	
	return {
		image: personalInfoData.profile_image_url || personalInfoData.image || null,
		document: personalInfoData.resume_url || personalInfoData.document || null,
		personalInfo: {
			firstName: personalInfoData.firstName || personalInfoData.first_name || '',
			middleName: personalInfoData.middleName || personalInfoData.middle_name || '',
			lastName: personalInfoData.lastName || personalInfoData.last_name || '',
			dateOfBirth: parseLocalDate(personalInfoData.dateOfBirth || personalInfoData.date_of_birth),
			gender: personalInfoData.gender || '',
			nationality: personalInfoData.nationality || '',
			email: personalInfoData.email || '',
			phone: personalInfoData.phone || '',
			mailingAddress: {
				address: personalInfoData.mailing_address || personalInfoData.mailingAddress?.address || '',
				city: personalInfoData.mailing_city || personalInfoData.mailingAddress?.city || '',
				state: personalInfoData.mailing_state || personalInfoData.mailingAddress?.state || '',
				country: personalInfoData.mailing_country || personalInfoData.mailingAddress?.country || '',
				zipCode: personalInfoData.mailing_zip_code || personalInfoData.mailingAddress?.zipCode || '',
			},
			useMailingAsPermanent: personalInfoData.use_mailing_as_permanent !== false,
			permanentAddress: {
				address: personalInfoData.permanent_address || personalInfoData.permanentAddress?.address || '',
				city: personalInfoData.permanent_city || personalInfoData.permanentAddress?.city || '',
				state: personalInfoData.permanent_state || personalInfoData.permanentAddress?.state || '',
				country: personalInfoData.permanent_country || personalInfoData.permanentAddress?.country || '',
				zipCode: personalInfoData.permanent_zip_code || personalInfoData.permanentAddress?.zipCode || '',
			},
		},
	};
};

/**
 * Map educational background from API response
 */
export const mapEducationalBackground = (educationData) => {
	if (!educationData || !Array.isArray(educationData)) return {};
	
	const education = educationData[0] || {};
	return {
		degree: education.degree || '',
		fieldOfStudy: education.field_of_study || '',
		graduationYear: education.graduation_year || '',
		institutionName: education.institution_name || '',
		city: education.city || '',
		state: education.state || '',
		country: education.country || '',
		webpage: education.institution_webpage || '',
		file: education.certificate_url || null,
	};
};

/**
 * Map professional experience from API response
 */
export const mapProfessionalExperience = (experienceData) => {
	if (!experienceData || !Array.isArray(experienceData)) return {};
	return experienceData[0] || {};
};

/**
 * Parse JSON or return original value
 */
const parseJSON = (value, fallback = []) => {
	if (!value) return fallback;
	if (typeof value === 'string') {
		try {
			return JSON.parse(value);
		} catch (e) {
			console.warn('Failed to parse JSON:', e);
			return fallback;
		}
	}
	return value;
};

/**
 * Map fellowship information from API response
 */
export const mapFellowshipInfo = (fellowshipInfoData) => {
	if (!fellowshipInfoData) return {};
	
	// Convert themes array to object format
	let themesObject = {};
	if (fellowshipInfoData.themes) {
		const themesArray = parseJSON(fellowshipInfoData.themes, []);
		
		if (Array.isArray(themesArray)) {
			themesArray.forEach(theme => {
				const key = theme === 'Others' ? 'others' : theme;
				themesObject[key] = true;
			});
		}
	}
	
	return {
		...fellowshipInfoData,
		themes: themesObject,
		programAppliedFor: fellowshipInfoData.program || '',
		othersField: fellowshipInfoData.other_option || '',
		bookProposal: fellowshipInfoData.book_proposal_url || null,
		writingSample: fellowshipInfoData.writing_sample_url || null,
	};
};

/**
 * Map additional information from API response
 */
export const mapAdditionalInfo = (additionalInfoData) => {
	if (!additionalInfoData) return {};
	
	const publications = parseJSON(additionalInfoData.publications, []);
	const socialMedia = parseJSON(additionalInfoData.social_profiles, []);
	
	// Map publications and convert file_url to file property for consistency
	const mappedPublications = publications.length > 0 
		? publications.map(pub => ({
			name: pub.name || '',
			url: pub.url || '',
			// Use file_url if available (from database), otherwise use file property
			file: pub.file_url || pub.file || null,
		}))
		: [{ name: '', url: '', file: null }];
	
	return {
		publications: mappedPublications,
		socialMedia: socialMedia.length > 0 ? socialMedia : [{ platform: '', url: '' }],
		termsAccepted: !!additionalInfoData.terms_accepted,
	};
};

/**
 * Map complete application data from API response
 */
export const mapApplicationData = (savedData) => {
	const processedData = {};
	
	if (savedData.personalInfo) {
		processedData.personalInfo = mapPersonalInfo(savedData.personalInfo);
	}
	
	if (savedData.education) {
		processedData.educationalBackground = mapEducationalBackground(savedData.education);
	}
	
	if (savedData.professional_experience) {
		processedData.professionalExperience = mapProfessionalExperience(savedData.professional_experience);
	}
	
	if (savedData.referees) {
		processedData.recommendations = mapRefereeData(savedData.referees);
	} else {
		processedData.recommendations = {
			recommendation1: {},
			recommendation2: {},
		};
	}
	
	if (savedData.fellowshipInfo) {
		processedData.fellowshipInformation = mapFellowshipInfo(savedData.fellowshipInfo);
	}
	
	if (savedData.additionalInfo) {
		processedData.additionalInformation = mapAdditionalInfo(savedData.additionalInfo);
	}
	
	return processedData;
};
